import { forwardRef, type ForwardedRef, useId, useRef, useState, type TextareaHTMLAttributes, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { GripSize24 } from '@hh.ru/magritte-ui-icon/private';
import { useInputAriaProps } from '@hh.ru/magritte-ui-input/useInputAriaProps';
import type { ControlledTextAreaProps } from '@hh.ru/magritte-ui-textarea/types';
import { useFocus } from '@hh.ru/magritte-ui-textarea/useFocus';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './textarea.less';

const MIN_HEIGHT_WITH_VISIBLE_LIMIT = 112;

const TextAreaComponent = (
    {
        onChange,
        onFocus,
        onBlur,
        onValidate,
        value,
        placeholder,
        disabled = false,
        elevatePlaceholder = false,
        invalid = false,
        invalidLimit = false,
        maxLength,
        description,
        errorMessage,
        resize = 'none',
        layout = 'fixed',
        minHeight,
        ...restProps
    }: ControlledTextAreaProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof ControlledTextAreaProps>,
    ref: ForwardedRef<HTMLTextAreaElement>
) => {
    const { isXS, isS } = useBreakpoint();
    const [internalInvalid, setInternalInvalid] = useState(false);
    const [isFocusVisible, setFocusVisible] = useState(false);
    const limitRef = useRef<HTMLDivElement>(null);
    const previousInvalid = useRef(internalInvalid);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const resizableDivRef = useRef<HTMLDivElement>(null);
    const resizableDivSizes = useRef<{ width?: number; height?: number } | null>(null);
    const resizableDivWasResized = useRef(false);
    const textAreaRefCallback = useMultipleRefs(ref, textAreaRef);
    const descriptionId = useId();
    const labelId = useId();
    const [animationTimeout, setAnimationTimeout] = useState(100);
    const currentResizeValue = isXS || isS ? 'none' : resize;
    const isResizable = currentResizeValue !== 'none';
    const [isMaxLengthDisplayed, setMaxLengthDisplayed] = useState(!!maxLength && !minHeight);
    const actualMinHeight = minHeight !== undefined && !value.includes('\n') ? minHeight : undefined;

    const forceDisabled = useDisabled();
    const isComponentDisabled = forceDisabled || !!disabled;

    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.textareaAnimationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const animationTimeout = parseInt(style.getPropertyValue(`--animation-duration`), 10);
        if (Number.isInteger(animationTimeout)) {
            setAnimationTimeout(animationTimeout);
        }
        document.body.removeChild(animationTimeoutElement);
    }, []);

    const { focusHandler, blurHandler, isFocused } = useFocus<HTMLTextAreaElement>(onFocus, onBlur);

    const handleClick = () => {
        if (resizableDivWasResized.current) {
            resizableDivWasResized.current = false;
            return;
        }
        textAreaRef.current?.focus();
    };

    const handleMouseUp = () => {
        if (
            resizableDivRef.current?.offsetWidth !== resizableDivSizes.current?.width ||
            resizableDivRef.current?.offsetHeight !== resizableDivSizes.current?.height
        ) {
            resizableDivWasResized.current = true;
        }
    };

    const handleMouseDown = () => {
        resizableDivSizes.current = {
            width: resizableDivRef.current?.offsetWidth,
            height: resizableDivRef.current?.offsetHeight,
        };
    };

    useEffect(() => {
        if (maxLength) {
            const invalid = !isFocused && value.length > maxLength;
            setInternalInvalid(invalid);

            if (previousInvalid.current !== invalid) {
                onValidate({ invalidLimit: invalid });
            }
            previousInvalid.current = invalid;
        }
    }, [onValidate, value, internalInvalid, maxLength, isFocused]);

    useEffect(() => {
        if (textAreaRef.current) {
            setFocusVisible(isFocused && textAreaRef.current.classList.contains('focus-visible'));
        }
    }, [isFocused]);

    const placeholderIsElevated = !!elevatePlaceholder && !!placeholder;
    const showLabel = placeholderIsElevated || value === '';

    const invalidState = invalid || invalidLimit;
    const ariaProps = useInputAriaProps({
        invalid: invalidState || internalInvalid,
        hasDescription: !!description,
        hasPlaceholder: !!placeholder,
        descriptionId,
        labelId,
    });

    const hasElevatedPlaceholderClass = (isFocused || value !== '') && placeholderIsElevated;

    useEffect(
        () => {
            if (resizableDivRef.current !== null) {
                setMaxLengthDisplayed(
                    !!maxLength && (!minHeight || resizableDivRef.current.offsetHeight >= MIN_HEIGHT_WITH_VISIBLE_LIMIT)
                );
            }
        },
        // value нужен в зависимостях, чтобы проверять размер после ввода каждого символа
        [maxLength, minHeight, value]
    );

    return (
        <div
            data-qa="textarea-wrapper"
            data-interactive={true}
            className={classnames(styles.textarea, {
                [styles.textareaFocused]: isFocused,
                [styles.textareaDisabled]: isComponentDisabled,
                [styles.textareaInvalid]: !!(invalidState || internalInvalid),
                [styles.textareaLimitError]:
                    invalidLimit || internalInvalid || (maxLength ? value.length > maxLength : false),
                [styles.textareaLimited]: isMaxLengthDisplayed,
                [styles.textareaLayoutFillBoth]: layout === 'fill-both',
                [styles.textareaLayoutFillVertical]: layout === 'fill-vertical',
                [styles.textareaLayoutFillHorizontal]: layout === 'fill-horizontal',
                [styles.textareaLayoutHug]: layout === 'hug',
                [styles.textareaElevated]: placeholderIsElevated,
                [styles[`minHeight${minHeight}`]]: actualMinHeight,
                [styles.minHeightDefault]: !minHeight,
            })}
        >
            <div
                className={classnames(styles.textareaPositionContainerWrapper, {
                    'focus-visible': isFocusVisible,
                })}
            >
                <div
                    className={classnames(styles.textareaPositionContainer)}
                    onClick={handleClick}
                    onMouseDown={isResizable ? handleMouseDown : undefined}
                    onMouseUp={isResizable ? handleMouseUp : undefined}
                    style={{ resize: currentResizeValue }}
                    ref={resizableDivRef}
                >
                    <div
                        className={styles.textareaWrapper}
                        data-qa="textarea-native-wrapper"
                        data-replicated-value={layout === 'hug' ? value : ''}
                    >
                        <textarea
                            {...restProps}
                            {...ariaProps}
                            disabled={isComponentDisabled}
                            suppressHydrationWarning
                            className={styles.nativeElement}
                            value={value}
                            onChange={onChange}
                            onFocus={focusHandler}
                            onBlur={blurHandler}
                            ref={textAreaRefCallback}
                            rows={actualMinHeight && 1}
                        />
                    </div>
                    {showLabel && (
                        <div className={styles.textareaRiggingContainer}>
                            <label
                                className={classnames(styles.textareaLabel, {
                                    [styles.textareaLabelElevated]: hasElevatedPlaceholderClass,
                                })}
                                id={ariaProps['aria-labelledby']}
                            >
                                {placeholder}
                            </label>
                        </div>
                    )}
                    {!!maxLength && isMaxLengthDisplayed && (
                        <CSSTransition
                            in={isFocused || value.length > maxLength}
                            timeout={animationTimeout}
                            classNames={{
                                enter: styles.textareaLimitEnter,
                                enterActive: styles.textareaLimitEnterActive,
                                exit: styles.textareaLimitExit,
                                exitActive: styles.textareaLimitExitActive,
                            }}
                            unmountOnExit
                            nodeRef={limitRef}
                        >
                            <div className={styles.textareaLimit} ref={limitRef}>
                                <Text typography="label-3-regular">
                                    {value.length}/{maxLength}
                                </Text>
                            </div>
                        </CSSTransition>
                    )}
                </div>
                {currentResizeValue !== 'none' && (
                    <div className={styles.textareaGrip}>
                        <div className={styles.textareaGripIcon}>
                            <GripSize24 />
                        </div>
                    </div>
                )}
            </div>
            <FormHelper
                description={description}
                errorMessage={errorMessage}
                descriptionId={ariaProps['aria-describedby']}
                disabled={isComponentDisabled}
                invalid={invalidState}
                data-qa="textarea-hint-container"
            />
        </div>
    );
};

const TextArea = forwardRef(TextAreaComponent);

TextArea.displayName = 'TextArea';

export { TextArea };
